import Button from 'components/Button/Button';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectUserActiveEmployerBusinessId } from 'store/user/selectors.js/user.selectors';
import { getChatbotAnalyticsControlData } from '../ChatbotAnalytics/store/slices/chatbotAnalyticsSlice';
import { ConfirmSubscribeDialog } from './ConfirmSubscribeDialog/ConfirmSubscribeDialog';

export function BuyChatbot() {
  const dispatch = useDispatch();

  const chatbotAnalyticsControlData = useSelector(
    getChatbotAnalyticsControlData
  );
  const businessId = useSelector(selectUserActiveEmployerBusinessId);

  const isChatbotAlreadyLicencedLicensed =
    chatbotAnalyticsControlData.chatbotLicencess?.some(
      (lic) => lic.businessId === businessId && lic.deleted === 'FALSE'
    );

  const [isConfirmSubscriptionDialogOpen, setIsConfirmSubscriptionDialogOpen] =
    useState(false);
  const [hoverGhost, setHoverGhost] = useState(false);

  const styles = useMemo(
    () => ({
      page: {
        minHeight: '100vh',
        background:
          'radial-gradient(1200px 600px at 10% -10%, #1b2a4a 0%, transparent 60%), radial-gradient(1000px 500px at 110% 10%, #3a1f5d 0%, transparent 60%), linear-gradient(180deg, #0b0f17 0%, #0b0f17 100%)',
        color: '#e6e9ef',
        fontFamily:
          "Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, 'Apple Color Emoji', 'Segoe UI Emoji'",
        display: 'flex',
        flexDirection: 'column',
      },
      container: {
        width: '100%',
        maxWidth: 1180,
        margin: '0 auto',
        padding: '32px 24px 64px',
      },
      nav: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        padding: '16px 0',
      },
      brand: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      },
      logo: {
        width: 40,
        height: 40,
        borderRadius: 10,
        background:
          'conic-gradient(from 180deg at 50% 50%, #6ee7ff, #7c3aed, #22d3ee, #6ee7ff)',
        boxShadow: '0 0 24px rgba(124, 58, 237, 0.5)',
      },
      brandText: {
        fontWeight: 700,
        letterSpacing: 0.2,
        fontSize: 18,
      },
      ghostBtn: (hovered) => ({
        padding: '10px 14px',
        borderRadius: 12,
        border: '1px solid rgba(255,255,255,0.14)',
        background: hovered ? 'rgba(255,255,255,0.08)' : 'transparent',
        color: '#cbd5e1',
        cursor: 'pointer',
        transition: 'all 200ms ease',
        backdropFilter: 'blur(6px)',
      }),
      hero: {
        marginTop: 32,
        display: 'grid',
        gridTemplateColumns: '1.15fr 0.85fr',
        gap: 28,
      },
      heroCol: {
        display: 'flex',
        flexDirection: 'column',
        gap: 18,
      },
      badge: {
        alignSelf: 'flex-start',
        padding: '6px 10px',
        fontSize: 12,
        borderRadius: 999,
        background: 'rgba(16,185,129,0.15)',
        color: '#86efac',
        border: '1px solid rgba(16,185,129,0.25)',
      },
      h1: {
        fontSize: 48,
        lineHeight: 1.1,
        fontWeight: 800,
        margin: 0,
        letterSpacing: -0.6,
        background: 'linear-gradient(180deg, #ffffff 0%, #cbd5e1 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
      },
      sub: {
        fontSize: 18,
        color: '#a8b3cf',
        lineHeight: 1.6,
        marginTop: 6,
      },
      heroCard: {
        alignSelf: 'stretch',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: 22,
        borderRadius: 16,
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow:
          '0 10px 30px rgba(2, 6, 23, 0.5), inset 0 1px 0 rgba(255,255,255,0.06)',
        backdropFilter: 'blur(8px)',
      },
      priceRow: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: 12,
      },
      price: {
        fontSize: 44,
        fontWeight: 800,
        letterSpacing: -0.5,
        lineHeight: 1,
        color: '#ffffff',
      },
      per: { color: '#94a3b8', marginBottom: 6 },
      ul: {
        listStyle: 'none',
        padding: 0,
        margin: '4px 0 0 0',
        display: 'grid',
        gap: 10,
      },
      li: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        color: '#cbd5e1',
        fontSize: 15,
      },
      check: {
        width: 18,
        height: 18,
        borderRadius: 6,
        background: 'linear-gradient(180deg, #1687af, #1687af)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        // boxShadow: '0 6px 18px rgba(34, 211, 238, 0.45)',
      },
      btnPrimary: (hovered) => ({
        width: '100%',
        padding: '14px 18px',
        border: 'none',
        borderRadius: 14,
        fontWeight: 700,
        letterSpacing: 0.2,
        cursor: 'pointer',
        background: hovered
          ? 'linear-gradient(180deg, #a78bfa, #7c3aed)'
          : 'linear-gradient(180deg, #22d3ee, #7c3aed)',
        color: '#0b0f17',
        boxShadow: hovered
          ? '0 14px 30px rgba(124, 58, 237, 0.45)'
          : '0 12px 28px rgba(34, 211, 238, 0.35)',
        transition: 'all 180ms ease',
      }),
      trust: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 12,
        alignItems: 'center',
        color: '#94a3b8',
        fontSize: 13,
      },
      trustBadge: {
        padding: '6px 10px',
        borderRadius: 8,
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
      },
      grid: {
        marginTop: 40,
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: 18,
      },
      card: {
        padding: 20,
        borderRadius: 16,
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 10px 28px rgba(2, 6, 23, 0.5)',
        backdropFilter: 'blur(8px)',
      },
      cardTitle: { fontWeight: 700, marginBottom: 8 },
      cardText: { color: '#a8b3cf', lineHeight: 1.6 },
      faq: {
        marginTop: 28,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 18,
      },
      footer: {
        marginTop: 48,
        paddingTop: 24,
        borderTop: '1px solid rgba(255,255,255,0.08)',
        color: '#8b9bb5',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
      },
      remark: { fontSize: 12, color: '#8b9bb5' },
      // Mobile stacking without media queries by allowing grid to auto-fit when narrow
      autoFitGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: 18,
      },
    }),
    []
  );

  const closeDialog = () => {
    console.log('closeDialog');

    setIsConfirmSubscriptionDialogOpen(false);
  };

  const handleSubscribe = () => {
    setIsConfirmSubscriptionDialogOpen(true);
  };

  return (
    <main style={styles.page}>
      <div style={styles.container}>
        {/* Nav */}
        <nav style={styles.nav}>
          <div style={styles.brand}>
            <div style={styles.logo} />
            <span style={styles.brandText}>Nerobotti lisäosa</span>
          </div>
          {/* <button
            onMouseEnter={() => setHoverGhost(true)}
            onMouseLeave={() => setHoverGhost(false)}
            style={styles.ghostBtn(hoverGhost)}
          >
            Lisätiedot
          </button> */}
        </nav>

        {/* Hero */}
        <section style={styles.hero}>
          <div style={styles.heroCol}>
            <span style={styles.badge}>
              Uusi • Tee Lixanista vielä parempi Nerobotin avulla
            </span>
            <h1 style={styles.h1}>
              Lixaniisi on nyt saatavilla Nerobotti lisäosa
            </h1>
            <p style={styles.sub}>
              Etsi projektipankin aineistosta tietoa Nerobotin avulla. Nerobotti
              hakee sinulle oleelliset tiedot projektipankin tiedostoista ja
              muodostaa niistä tiivistelmän.
            </p>
            <div style={styles.heroCard}>
              <div style={styles.priceRow}>
                <div style={styles.price}>300€</div>
                <div style={styles.per}>/ kuukaudessa • rajattomat viestit</div>
              </div>
              <ul style={styles.ul}>
                {[
                  'Rajoittamaton käyttö yrityksellesi',
                  'Ei vaadi erillistä kirjautumista',
                ].map((t) => (
                  <li key={t} style={styles.li}>
                    <span style={styles.check}>
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M20 6L9 17l-5-5"
                          stroke="#0b0f17"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    {t}
                  </li>
                ))}
              </ul>
              <Button color="primary" onClick={handleSubscribe}>
                Lisää Lixaniin vain 300€/kk
              </Button>

              <span style={styles.remark}>Hinta + alv</span>
            </div>
          </div>

          {/* Visual/Mock side */}
          <aside style={styles.heroCard}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 10,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 999,
                    background: '#ef4444',
                  }}
                />
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 999,
                    background: '#f59e0b',
                  }}
                />
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 999,
                    background: '#22c55e',
                  }}
                />
              </div>
              <span style={{ fontSize: 12, color: '#93a4bf' }}>
                Lixani • Nerobotti
              </span>
            </div>
            <img
              alt="chatbot-button"
              src="/images/nerobotti-kys.png"
              style={{ width: '100%', borderRadius: '16px' }}
            />
          </aside>
        </section>

        {/* Feature Grid */}
        <section style={styles.grid}>
          {[
            {
              title: 'Täysi integraatio Lixaniin',
              text: 'Koko projektipankin aineisto hallussa.',
            },
            {
              title: 'Rajoittamattomat kysymykset',
              text: 'Selkeä laskutus. Yksi hinta ilman käyttörajoituksia.',
            },
            {
              title: 'Tuotantovalmis kokonaisuus',
              text: 'Analytiikan avulla näet, mitä Nerobotilta on järjestelmässäsi kysytty ja ovatko vastaukset olleet tyydyttäviä.',
            },
          ].map(({ title, text }) => (
            <article key={title} style={styles.card}>
              <h3 style={styles.cardTitle}>{title}</h3>
              <p style={styles.cardText}>{text}</p>
            </article>
          ))}
        </section>

        {/* FAQ */}
        <section style={{ marginTop: 24 }}>
          <h3 style={{ fontSize: 18, marginBottom: 12 }}>FAQ</h3>
          <div style={styles.faq}>
            <article style={styles.card}>
              <h4 style={styles.cardTitle}>Miten laskutamme?</h4>
              <p style={styles.cardText}>
                Kuukausittainen tilaus 300€ + alv. Ei irtisanomisaikaa.
              </p>
            </article>
            <article style={styles.card}>
              <h4 style={styles.cardTitle}>Näkeekö Nerobotti kaiken datan?</h4>
              <p style={styles.cardText}>
                Nerobotti osaa lukea projektipankista aineistot, jotka ovat
                seuraavissa tiedostomuodoissa: pdf, xls, xlsx, txt, doc, docx.
              </p>
            </article>
          </div>
        </section>
      </div>

      {isConfirmSubscriptionDialogOpen ? (
        <ConfirmSubscribeDialog onClose={closeDialog}></ConfirmSubscribeDialog>
      ) : null}
    </main>
  );
}
